<script lang="ts" setup>
import { PropType, ref, computed, onBeforeUnmount } from "vue";
import {
  type DocumentSpaceSection,
  type DocumentSpaceItem,
  type DocumentSpace,
  SpaceItemMoveType,
  useMoveSpaceItemMutation,
  useCreateDocumentSpaceItemMutation,
  useMoveSpaceItemToSectionMutation,
} from "@/graphql/operations";
import { DocumentSearchModal, Tooltip, Draggable } from "@/components";
import DocumentList from "./DocumentList.vue";
import CreateSectionModal from "./CreateSectionModal.vue";
import SectionControls from "./SectionControls.vue";
import FlowPickerModal from "@/views/document_spaces/detail/FlowPickerModal.vue";
import { useStore } from "vuex";
import type {
  DeleteReconciledItem,
  IsItemDeletionPending,
} from "@/composables/useReconciledItemDeletion";

const props = defineProps({
  sections: {
    type: Array as PropType<Array<DocumentSpaceSection>>,
    required: true,
  },
  documentSpaceId: {
    type: String,
    required: true,
  },
  documentSpace: {
    type: Object as PropType<DocumentSpace>,
    required: true,
  },
  emailVerificationValid: {
    type: Boolean,
    required: true,
  },
  deleteItem: {
    type: Function as PropType<DeleteReconciledItem>,
    required: true,
  },
  isItemDeletionPending: {
    type: Function as PropType<IsItemDeletionPending>,
    required: true,
  },
});

const emits = defineEmits(["success"]);

const handleSuccess = () => {
  emits("success");
};
const { executeMutation: createDocumentSpaceItem } =
  useCreateDocumentSpaceItemMutation();

const addingDocuments = ref(false);
const addError = ref(false);
const addedInSearch = new Set<string>();
let mounted = true;
onBeforeUnmount(() => {
  mounted = false;
});
const addDocuments = async (docIds: Array<string>) => {
  const targetSectionId = sectionId.value;
  if (!targetSectionId || addingDocuments.value || docIds.length === 0) return;
  addingDocuments.value = true;
  addError.value = false;
  try {
    for (const docId of docIds) {
      if (addedInSearch.has(docId)) continue;
      const result = await createDocumentSpaceItem({
        docId,
        spaceId: props.documentSpaceId,
        sectionId: targetSectionId,
      });
      if (!mounted) return;
      if (result.error || !result.data?.createDocumentSpaceItem) {
        addError.value = true;
        return;
      }
      addedInSearch.add(docId);
      handleSuccess();
    }
    displaySearch.value = false;
  } finally {
    addingDocuments.value = false;
  }
};

const displaySearch = ref<boolean>(false);
const sectionId = ref<string>();

const openSearch = (id: string) => {
  addedInSearch.clear();
  addError.value = false;
  sectionId.value = id;
  displaySearch.value = true;
};
const closeSearch = () => {
  if (!addingDocuments.value) displaySearch.value = false;
};

// Custom Flow picker, opened from the document search header
const store = useStore();
const canAddCustomFlows = computed(
  () => store.getters.user?.canShareCustomFlowsInSharedSpaces?.() ?? false,
);
const showFlowPicker = ref<boolean>(false);

const onFlowsAdded = () => {
  showFlowPicker.value = false;
  closeSearch();
  handleSuccess();
};

const documentIds = computed(() =>
  props.sections
    .flatMap((s) => s!.documentSpaceItemsBySectionId!.nodes!)
    .map((d) => d?.document?.id)
    .filter((id): id is string => !!id),
);

const itemCount = computed(() =>
  props.sections.reduce(
    (sum, s) => sum + (s!.documentSpaceItemsBySectionId!.nodes!.length ?? 0),
    0,
  ),
);

const displayCreateModal = ref<boolean>(false);

const { executeMutation: moveItem } = useMoveSpaceItemMutation();
const placeAbove = async (id: string, targetId: string) => {
  const res = await moveItem({
    withId: id,
    targetItemId: targetId,
    location: SpaceItemMoveType.Before,
  });

  if (res.error) {
  } else {
    handleSuccess();
  }
};

const placeBelow = async (id: string, targetId: string) => {
  const res = await moveItem({
    withId: id,
    targetItemId: targetId,
    location: SpaceItemMoveType.After,
  });

  if (res.error) {
  } else {
    handleSuccess();
  }
};

const { executeMutation: moveItemToSection } =
  useMoveSpaceItemToSectionMutation();
const moveToSection = async (itemId: string, sectionId: string) => {
  const res = await moveItemToSection({
    withId: itemId,
    targetSectionId: sectionId,
  });

  if (res.error) {
  } else {
    handleSuccess();
  }
};

const hiddenSections = ref<Array<string>>([]);
const toggleVisibility = (sectionId: string) => {
  if (hiddenSections.value.includes(sectionId)) {
    hiddenSections.value.splice(hiddenSections.value.indexOf(sectionId), 1);
  } else {
    hiddenSections.value.push(sectionId);
  }
};

const sectionNames = computed(() => props.sections.map((s) => s.title));

const crmContactId = computed(
  () =>
    props.documentSpace?.documentSpaceCrmMapping?.crmContact?.id ?? undefined,
);
</script>

<template>
  <div class="container">
    <Draggable
      @placeBelow="placeBelow"
      @placeAbove="placeAbove"
      :numItems="itemCount"
      @placeInDropzone="moveToSection"
    >
      <div v-for="(section, idx) in sections" class="content_container">
        <SectionControls
          :title="section.title"
          :id="section.id"
          :spaceId="documentSpaceId"
          :numSections="sections.length"
          :sectionIdx="idx"
          :numItems="section.documentSpaceItemsBySectionId.nodes.length"
          :sectionNames="sectionNames"
          @success="handleSuccess"
          @toggleVisibility="toggleVisibility(section!.id)"
          :isVisible="!hiddenSections.includes(section!.id)"
        />

        <template v-if="!hiddenSections.includes(section!.id)">
          <template
            v-if="section.documentSpaceItemsBySectionId.nodes.length === 0"
          >
            <div class="dropzone" :data-id="section!.id" data-dropzone>
              <button
                type="button"
                class="action_row"
                data-test="add-shared-space-content"
                @click="openSearch(section.id)"
              >
                <span class="material-symbols-outlined" aria-hidden="true">
                  add
                </span>
                <div class="cell">
                  {{ $t("documentSpace.addDocument.buttonText") }}
                </div>
              </button>
            </div>
          </template>

          <template v-else>
            <button
              type="button"
              class="action_row"
              data-test="add-shared-space-content"
              @click="openSearch(section.id)"
            >
              <span class="material-symbols-outlined" aria-hidden="true">
                add
              </span>
              <div class="cell">
                {{ $t("documentSpace.addDocument.buttonText") }}
              </div>
            </button>

            <DocumentList
              :documents="
                section.documentSpaceItemsBySectionId
                  .nodes as Array<DocumentSpaceItem>
              "
              :documentSpaceId="documentSpaceId"
              :documentSpace="documentSpace"
              :emailVerificationValid="emailVerificationValid"
              :deleteItem="deleteItem"
              :isItemDeletionPending="isItemDeletionPending"
            />
          </template>
        </template>
      </div>
    </Draggable>

    <button
      type="button"
      class="link"
      data-test="add-shared-space-section"
      @click="displayCreateModal = true"
    >
      <span class="material-symbols-outlined" aria-hidden="true">
        playlist_add
      </span>
      {{ $t("documentSpace.sections.add") }}
    </button>
  </div>
  <template v-if="displaySearch">
    <DocumentSearchModal
      :busy="addingDocuments"
      @close="closeSearch"
      @submit="addDocuments"
      :onlyShareable="true"
      :excluded="documentIds"
      :crmContactId="crmContactId"
      :documentSpaceId="documentSpaceId"
    >
      <template #feedback>
        <p v-if="addError" role="alert" class="save_error">{{ $t("error") }}</p>
      </template>
      <template v-if="canAddCustomFlows" #top-action>
        <button class="add-flow-button" @click="showFlowPicker = true">
          <span class="material-symbols-outlined">layers</span>
          {{ $t("sharedUi.flowPicker.title") }}
        </button>
      </template>
    </DocumentSearchModal>
  </template>

  <FlowPickerModal
    v-if="showFlowPicker && sectionId"
    :space-id="documentSpaceId"
    :section-id="sectionId"
    :space-name="documentSpace.name ?? ''"
    @close="showFlowPicker = false"
    @added="onFlowsAdded"
  />

  <CreateSectionModal
    v-if="displayCreateModal"
    :documentSpaceId="documentSpaceId"
    :sectionNames="sectionNames"
    @close="displayCreateModal = false"
    @success="handleSuccess"
  />
</template>

<style lang="scss" scoped>
.save_error {
  color: var(--classic-danger, #c62828);
  font-size: 0.9em;
}
.add-flow-button {
  margin-bottom: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--classic-brand-primary, $color-primary);
  background: transparent;
  border: 1px solid var(--classic-brand-primary, $color-primary);
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: var(--classic-accent-soft, rgba(219, 0, 110, 0.08));
  }

  .material-symbols-outlined {
    font-size: 18px;
  }
}

.container {
  padding: 1em;
}

.content_container {
  margin-bottom: 1.8em;
}

.action_row {
  background: var(--classic-surface-hover, #fafafa);
  border: 0;
  color: inherit;
  font-family: inherit;
  text-align: left;
  width: 100%;
  display: flex;
  padding: 0.5em 0;
  font-size: 0.9rem;
  align-items: center;
}

.action_row:hover {
  cursor: pointer;
}

.action_row .cell {
  padding: 0 0.5em 0 1em;
  line-height: 2.4em;
  flex: 1;
  color: var(--classic-accent, $color-secondary);
}

.action_row .material-symbols-outlined {
  color: var(--classic-accent, $color-secondary);
}

.link {
  text-decoration: none;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--classic-accent, $color-secondary);
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  margin-top: 2.5em;
  margin-bottom: 1em;
  font-size: 1.1em;
  font-weight: 500;
}

.link .material-symbols-outlined {
  margin-right: 0.3em;
}
</style>
